

import { GoogleGenAI, Type } from '@google/genai';

let ai;

// This function ensures ai is initialized only once.
const getAI = () => {
    if (!ai) {
        // Vite uses import.meta.env for environment variables
        // Supports VITE_API_KEY, VITE_GEMINI_API_KEY, or GEMINI_API_KEY (if exposed via define)
        const apiKey = import.meta.env.VITE_API_KEY ||
            import.meta.env.VITE_GEMINI_API_KEY ||
            import.meta.env.GEMINI_API_KEY;
        if (!apiKey) {
            throw new Error("API_KEY environment variable not set. Please set VITE_API_KEY or VITE_GEMINI_API_KEY in your .env file.");
        }
        ai = new GoogleGenAI({ apiKey: apiKey });
    }
    return ai;
};


const tools = [
    {
        name: 'generate_study_flowchart',
        description: "Generates a 2-year or 4-year flowchart of courses. It creates a 2-year plan for an Associate degree at Miami Dade College (MDC). If a target university and bachelor's degree are provided, it extends the plan to a full 4-year timeline including the transfer path.",
        parameters: {
            type: Type.OBJECT,
            properties: {
                career: { type: Type.STRING, description: 'The desired career path or major, e.g., "Software Engineering".' },
                startDate: { type: Type.STRING, description: 'The desired start date for studies, e.g., "Fall 2024".' },
                coursesPerTerm: { type: Type.STRING, description: 'The number of courses the student wants to take per term. e.g., "3", or "4 in fall, 2 in summer".' },
                targetUniversity: { type: Type.STRING, description: 'Optional. The university the student wants to transfer to for a Bachelor\'s degree, e.g., "Florida International University".' },
                bachelorsDegree: { type: Type.STRING, description: 'Optional. The specific Bachelor\'s degree, e.g., "Bachelor of Science in Computer Science".' },
            },
            required: ['career', 'startDate', 'coursesPerTerm'],
        },
    },
    {
        name: 'offer_pdf_export',
        description: "Presents the user with options to download content as a PDF or send it to an email. Use this when the user wants to save or share a summary.",
        parameters: {
            type: Type.OBJECT,
            properties: {
                content: { type: Type.STRING, description: 'The text content to be included in the PDF.' },
            },
            required: ['content'],
        },
    },
    {
        name: 'analyze_career_potential',
        description: 'Analyzes a user\'s interests, skills, and optional resume to suggest and detail potential career paths. Use this for broad questions about what career to choose.',
        parameters: {
            type: Type.OBJECT,
            properties: {
                interests: { type: Type.STRING, description: 'A description of the user\'s interests, hobbies, and passions.' },
                skills: { type: Type.STRING, description: 'A description of the user\'s skills, both hard and soft.' },
                resumeText: { type: Type.STRING, description: 'Optional. The full text of the user\'s resume for a more detailed analysis.' },
            },
            required: ['interests', 'skills'],
        },
    },
    {
        name: 'get_tuition_estimate',
        description: 'Estimates tuition costs for a specific career path at a given university. Defaults to Miami Dade College if no university is specified.',
        parameters: {
            type: Type.OBJECT,
            properties: {
                career: { type: Type.STRING, description: 'The career path to estimate tuition for, e.g., "Nursing".' },
                university: { type: Type.STRING, description: 'Optional. The name of the university, e.g., "Florida International University". Defaults to "Miami Dade College".' },
            },
            required: ['career'],
        },
    },
    {
        name: 'get_course_summary',
        description: 'Provides a summary for a specific course within a career path. Uses Google Search to find details like prerequisites, topics covered, and difficulty.',
        parameters: {
            type: Type.OBJECT,
            properties: {
                career: { type: Type.STRING, description: 'The career path the course belongs to, e.g., "Computer Science".' },
                courseName: { type: Type.STRING, description: 'The name of the course to summarize, e.g., "Data Structures and Algorithms".' },
            },
            required: ['career', 'courseName'],
        },
    },
    {
        name: 'get_teacher_reviews',
        description: 'Fetches reviews for a specific teacher at Miami Dade College from sources like Rate My Professor using Google Search.',
        parameters: {
            type: Type.OBJECT,
            properties: {
                teacherName: { type: Type.STRING, description: 'The name of the teacher to look up.' },
                courseName: { type: Type.STRING, description: 'Optional. The name of the course the teacher teaches.' },
            },
            required: ['teacherName'],
        },
    },
    {
        name: 'find_teachers',
        description: 'Finds teachers at Miami Dade College based on specific criteria, such as the highest review score or most reviews. Can be filtered by course name.',
        parameters: {
            type: Type.OBJECT,
            properties: {
                sortBy: { type: Type.STRING, description: 'The criteria to sort teachers by. e.g., "highest score", "most reviews".' },
                courseName: { type: Type.STRING, description: 'Optional. The name of the course to filter teachers by.' },
            },
            required: ['sortBy'],
        },
    },
    {
        name: 'get_transfer_options',
        description: 'Provides information about transfer programs from Miami Dade College (MDC) to another university for a specific major.',
        parameters: {
            type: Type.OBJECT,
            properties: {
                major: { type: Type.STRING, description: 'The student\'s major, e.g., "Computer Science".' },
                targetUniversity: { type: Type.STRING, description: 'The university the student wants to transfer to, e.g., "Florida International University".' },
            },
            required: ['major', 'targetUniversity'],
        },
    },
    {
        name: 'calculate_degree_cost',
        description: 'Calculates the total cost of a degree plan for one or more universities using the College Financial API. Can get costs for a single university, compare multiple universities, or get program-specific costs.',
        parameters: {
            type: Type.OBJECT,
            properties: {
                university: { type: Type.STRING, description: 'The name of the university, e.g., "MIT", "University of Florida". Required for single university or program cost queries.' },
                universities: { type: Type.STRING, description: 'Comma-separated list of university names for comparison (up to 10). Use this instead of "university" when comparing multiple schools.' },
                program: { type: Type.STRING, description: 'Optional. The field of study or program name, e.g., "Computer Science", "Business".' },
                degree: { type: Type.STRING, description: 'Optional. Degree level - "Associate", "Bachelor", "Master", etc. Defaults to "Bachelor".' },
                years: { type: Type.STRING, description: 'Optional. Number of years to complete the degree as a string, e.g., "2", "4". Defaults to "4".' },
                adjustInflation: { type: Type.BOOLEAN, description: 'Optional. Whether to adjust costs for inflation over the degree period. Defaults to false.' },
            },
            required: [],
        },
    },
    {
        name: 'search_college_articulation_docs',
        description: 'Search the college articulation PDF vector database for information about articulation agreements, transfer credits, and college policies. Returns relevant document chunks with source citations.',
        parameters: {
            type: Type.OBJECT,
            properties: {
                query: { type: Type.STRING, description: 'The search query or question about college articulation agreements, transfer credits, or related policies.' },
                topK: { type: Type.INTEGER, description: 'Number of relevant chunks to retrieve (default: 5, max recommended: 10).' },
            },
            required: ['query'],
        },
    }
];

export const initializeChat = async () => {
    const ai = getAI();
    const chat = ai.chats.create({
        model: 'gemini-2.5-flash',
        config: {
            systemInstruction: `You are a friendly and knowledgeable AI career advisor for students, with a focus on Miami Dade College (MDC). Your goal is to provide helpful, accurate, and up-to-date information.

CRITICAL: When a user asks for a study plan, course plan, degree plan, or flowchart for a specific career, you MUST immediately call the 'generate_study_flowchart' tool. Do NOT provide a generic response. The tool will generate a detailed, term-by-term breakdown with actual course codes and names.

After calling generate_study_flowchart and receiving the data, present the full course plan to the user in a clear, organized format. Show ALL courses for each term. Then, offer to export the plan as a PDF using the 'offer_pdf_export' tool.

For users who are unsure where to start, you can offer to perform a detailed career potential analysis based on their interests and skills using the 'analyze_career_potential' tool.

When a user asks for information that could be time-sensitive or requires real-world data (like tuition, reviews, transfer options), use your available tools. Teacher reviews, tuition estimates, and transfer options should be specific to MDC. For transfer options, assume the student is completing an Associate in Arts (AA) at MDC.

Be encouraging and clear in your responses. Always provide complete, detailed information when using tools - never give vague or placeholder responses.`,
            tools: [{ functionDeclarations: tools }],
        }
    });
    return chat;
};

// FIX: Corrected chat.sendMessage call and refactored function call extraction.
export const sendMessageToAI = async (chat, message, toolResponse) => {
    let result;
    let content;

    if (toolResponse) {
        content = [
            {
                functionResponse: {
                    id: toolResponse.id,
                    name: toolResponse.name,
                    response: toolResponse.response,
                },
            },
        ];
    } else {
        content = [{ text: message }];
    }

    result = await chat.sendMessage({ message: content });

    // Extract function calls first
    const functionCalls = result.functionCalls?.map(fc => ({
        id: fc.id || '',
        name: fc.name,
        args: fc.args,
    }));

    // Extract text from response parts, handling cases where function calls are present
    // Avoid accessing result.text directly when function calls exist to prevent SDK warnings
    let text = '';
    if (result.candidates && result.candidates.length > 0) {
        const candidate = result.candidates[0];
        if (candidate.content && candidate.content.parts) {
            // Extract only text parts, ignoring function call parts
            const textParts = candidate.content.parts
                .filter(part => part.text && !part.functionCall)
                .map(part => part.text);
            text = textParts.join(' ').trim();
        }
    }

    // Only fallback to result.text if no function calls are present (to avoid SDK warning)
    // Check functionCalls length to avoid triggering SDK warning
    if (!text && (!functionCalls || functionCalls.length === 0)) {
        try {
            // Only access result.text if we know there are no function calls
            if (result.text) {
                text = result.text.trim();
            }
        } catch (e) {
            // Ignore any errors from accessing text property
            console.debug('Could not extract text from result:', e);
        }
    }

    const groundingChunks = result.candidates?.[0]?.groundingMetadata?.groundingChunks;

    return { text, functionCalls, groundingChunks };
};

export const getFlowchartData = async (career, startDate, coursesPerTerm, targetUniversity, bachelorsDegree) => {
    const ai = getAI();
    let prompt;

    if (targetUniversity && bachelorsDegree) {
        prompt = `Generate a comprehensive 4-year study plan for a student pursuing a career in ${career}. The plan should start with a 2-year Associate degree at Miami Dade College (MDC), beginning in ${startDate}, and then transfer to ${targetUniversity} to complete a ${bachelorsDegree}. Use Google Search to find the official MDC curriculum and the curriculum for the Bachelor's degree at ${targetUniversity}, including transfer requirements and articulation agreements like the 2+2 pathway. The student wants to take courses based on this schedule: "${coursesPerTerm}". Structure the plan to match this course load for all 4 years.

The output must be a single JSON object. The root object should have three keys:
1. "career" (string, value: "${career}")
2. "plans" (an array of two degree plan objects)
3. "extracurriculars" (an object with recommendations)

The first degree plan object (for MDC) must have three keys:
1. "institution" (string, value: "Miami Dade College")
2. "degree" (string, e.g., "Associate in Arts - ${career}")
3. "timeline" (an array of term objects for the first two years).

The second degree plan object (for ${targetUniversity}) must have three keys:
1. "institution" (string, value: "${targetUniversity}")
2. "degree" (string, value: "${bachelorsDegree}")
3. "timeline" (an array of term objects for the final two years).

Each term object in both timelines must have two keys: "term" (string, e.g., "Fall 2024") and "courses" (an array of strings, where each string includes both the course code and name, e.g., ["COP 1000 - Introduction to Programming"]).

The "extracurriculars" object should have two keys:
1. "clubs" (an array of 3-5 relevant clubs/organizations at MDC or ${targetUniversity}, e.g., ["Computer Science Club", "Robotics Team", "Women in Tech"])
2. "activities" (an array of 3-5 recommended extracurricular activities or opportunities, e.g., ["Participate in hackathons", "Join student government", "Volunteer at local tech companies"])

Use Google Search to find actual clubs and organizations available at MDC and ${targetUniversity} that are relevant to ${career}.

Do not include any explanatory text, just the raw JSON.`;
    } else {
        prompt = `Generate a detailed 2-year Associate degree study plan for a student at Miami Dade College (MDC) pursuing a career in ${career}, starting in ${startDate}. Use Google Search to find the official MDC curriculum for this pathway. The student wants to take courses based on this schedule: "${coursesPerTerm}". Structure the plan to match this course load.

The output must be a single JSON object. The root object should have three keys:
1. "career" (string, e.g., "${career}")
2. "plans" (an array containing one degree plan object)
3. "extracurriculars" (an object with recommendations)

The degree plan object must have three keys:
1. "institution" (string, value: "Miami Dade College")
2. "degree" (string, e.g., "Associate in Arts - ${career}")
3. "timeline" (an array of term objects).

Each term object in the timeline must have two keys: "term" (string, e.g., "Fall 2024") and "courses" (an array of strings, where each string includes both the course code and name, e.g., ["COP 1000 - Introduction to Programming"]).

The "extracurriculars" object should have two keys:
1. "clubs" (an array of 3-5 relevant clubs/organizations at MDC that are related to ${career}, e.g., ["Computer Science Club", "Engineering Society", "Student Government"])
2. "activities" (an array of 3-5 recommended extracurricular activities or opportunities, e.g., ["Participate in hackathons", "Volunteer in related field", "Attend industry conferences", "Join professional organizations"])

Use Google Search to find actual clubs and organizations available at Miami Dade College that are relevant to ${career}.

Do not include any explanatory text, just the raw JSON.`;
    }

    const response = await ai.models.generateContent({
        model: 'gemini-2.5-flash',
        contents: prompt,
        config: {
            tools: [{ googleSearch: {} }]
        }
    });

    try {
        let jsonText = response.text;

        // The model might wrap the JSON in markdown backticks or return it with other text.
        // Let's extract the JSON part.
        const jsonMatch = jsonText.match(/```(json)?\s*(\{[\s\S]*\})\s*```/);
        if (jsonMatch && jsonMatch[2]) {
            jsonText = jsonMatch[2];
        } else {
            // Fallback for when it's not in a markdown block but might be surrounded by text
            const startIndex = jsonText.indexOf('{');
            const endIndex = jsonText.lastIndexOf('}');
            if (startIndex !== -1 && endIndex !== -1) {
                jsonText = jsonText.substring(startIndex, endIndex + 1);
            }
        }

        const data = JSON.parse(jsonText);
        // Basic validation
        if (data.career && Array.isArray(data.plans)) {
            // Ensure extracurriculars exists, add default if missing
            if (!data.extracurriculars) {
                data.extracurriculars = {
                    clubs: [],
                    activities: []
                };
            }
            return data;
        } else {
            throw new Error("Invalid JSON structure received from AI for flowchart.");
        }
    } catch (e) {
        console.error("Failed to parse flowchart JSON:", e, "Raw text:", response.text);
        throw new Error("Could not generate a valid study plan. The AI returned an unexpected format.");
    }
};

export const analyzeCareerPotential = async (interests, skills, resumeText) => {
    const ai = getAI();
    let prompt = `Act as an expert career counselor. A student from Miami Dade College is seeking guidance. Analyze the following profile and provide a detailed career analysis.

    **Student's Interests:** ${interests}
    **Student's Skills:** ${skills}
    `

    if (resumeText) {
        prompt += `\n**Student's Resume:**\n${resumeText}\n`
    }

    prompt += `
    Based on this profile, suggest 3 to 5 highly relevant career paths. For each path, provide:
    1.  **Career Title:** (e.g., "Data Scientist")
    2.  **Why it's a good fit:** A brief explanation connecting their interests and skills to this career.
    3.  **Potential Starting Roles:** List 2-3 entry-level job titles.
    4.  **Key Skills to Develop:** Suggest 3-4 important skills they should focus on acquiring.
    5.  **Educational Path at MDC:** Recommend a relevant Associate's degree at Miami Dade College and mention potential transfer pathways for a Bachelor's degree.

    Format your response using clear headings and bullet points in Markdown. Be encouraging and provide actionable advice.`;

    const response = await ai.models.generateContent({
        model: 'gemini-2.5-flash',
        contents: prompt,
    });

    return response.text;
};

export const getTuitionEstimate = async (career, university) => {
    const ai = getAI();
    const targetUniversity = university || "Miami Dade College";
    const degreeType = targetUniversity === "Miami Dade College" ? "an Associate's degree" : "a Bachelor's degree";

    let prompt = `As a career advisor, provide a detailed estimate of tuition costs for a student pursuing ${degreeType} for a "${career}" career path at ${targetUniversity}.`;
    prompt += ` Use Google Search to find the most current and relevant information from the official ${targetUniversity} website. Break down the costs if possible (e.g., in-state vs. out-of-state, per credit hour, fees). Present the information clearly. If you find web sources, mention them.`;

    const response = await ai.models.generateContent({
        model: 'gemini-2.5-flash',
        contents: prompt,
        config: {
            tools: [{ googleSearch: {} }],
        },
    });

    let resultText = response.text;

    const groundingChunks = response.candidates?.[0]?.groundingMetadata?.groundingChunks;
    if (groundingChunks && groundingChunks.length > 0) {
        const sources = groundingChunks
            .map(chunk => chunk.web && chunk.web.uri ? `[${chunk.web.title || chunk.web.uri}](${chunk.web.uri})` : null)
            .filter(Boolean);
        if (sources.length > 0) {
            resultText += "\n\n**Sources:**\n- " + sources.join('\n- ');
        }
    }

    return resultText;
};


export const getCourseSummary = async (career, courseName) => {
    const ai = getAI();
    const prompt = `Provide a detailed summary for the course "${courseName}" in the context of a "${career}" degree. Use Google Search to find information on prerequisites, main topics covered, typical difficulty, and what students will learn. Present the information in a clear, structured format. If you find web sources, mention them.`;

    const response = await ai.models.generateContent({
        model: 'gemini-2.5-flash',
        contents: prompt,
        config: {
            tools: [{ googleSearch: {} }],
        },
    });

    let resultText = response.text;

    const groundingChunks = response.candidates?.[0]?.groundingMetadata?.groundingChunks;
    if (groundingChunks && groundingChunks.length > 0) {
        const sources = groundingChunks
            .map(chunk => chunk.web && chunk.web.uri ? `[${chunk.web.title || chunk.web.uri}](${chunk.web.uri})` : null)
            .filter(Boolean);
        if (sources.length > 0) {
            resultText += "\n\n**Sources:**\n- " + sources.join('\n- ');
        }
    }

    return resultText;
};

export const getTeacherReviews = async (teacherName, courseName) => {
    const ai = getAI();
    let prompt = `Using Google Search, find reviews for professor "${teacherName}" from "Miami Dade College".`;
    if (courseName) {
        prompt += ` Specifically in relation to the course "${courseName}".`;
    }
    prompt += ` Summarize the reviews found on websites like Rate My Professor. Focus on teaching style, overall quality, difficulty, and common student comments. If available, include their rating. Present the information clearly.`;

    const response = await ai.models.generateContent({
        model: 'gemini-2.5-flash',
        contents: prompt,
        config: {
            tools: [{ googleSearch: {} }],
        },
    });

    let resultText = response.text;

    const groundingChunks = response.candidates?.[0]?.groundingMetadata?.groundingChunks;
    if (groundingChunks && groundingChunks.length > 0) {
        const sources = groundingChunks
            .map(chunk => chunk.web && chunk.web.uri ? `[${chunk.web.title || chunk.web.uri}](${chunk.web.uri})` : null)
            .filter(Boolean);
        if (sources.length > 0) {
            resultText += "\n\n**Sources:**\n- " + sources.join('\n- ');
        }
    }

    return resultText;
};

export const findTeachers = async (sortBy, courseName) => {
    const ai = getAI();
    let prompt = `Using Google Search, find professors at "Miami Dade College" with the ${sortBy}.`;
    if (courseName) {
        prompt += ` for the course "${courseName}".`;
    }
    prompt += ` List the top 3-5 teachers found on sites like Rate My Professor, including their rating and a brief summary of their reviews. Format the response as a clear, easy-to-read list.`;

    const response = await ai.models.generateContent({
        model: 'gemini-2.5-flash',
        contents: prompt,
        config: {
            tools: [{ googleSearch: {} }],
        },
    });

    let resultText = response.text;

    const groundingChunks = response.candidates?.[0]?.groundingMetadata?.groundingChunks;
    if (groundingChunks && groundingChunks.length > 0) {
        const sources = groundingChunks
            .map(chunk => chunk.web && chunk.web.uri ? `[${chunk.web.title || chunk.web.uri}](${chunk.web.uri})` : null)
            .filter(Boolean);
        if (sources.length > 0) {
            resultText += "\n\n**Sources:**\n- " + sources.join('\n- ');
        }
    }

    return resultText;
};

export const getTransferOptions = async (major, targetUniversity) => {
    const ai = getAI();
    const prompt = `Provide detailed information about the transfer options and agreements between Miami Dade College (MDC) and ${targetUniversity} for a student with an Associate in Arts degree majoring in ${major}. Use Google Search to find official information from both MDC and ${targetUniversity} websites. Include details on articulation agreements, required GPA, and any specific prerequisite courses. Present the information clearly.`;

    const response = await ai.models.generateContent({
        model: 'gemini-2.5-flash',
        contents: prompt,
        config: {
            tools: [{ googleSearch: {} }],
        },
    });

    let resultText = response.text;

    const groundingChunks = response.candidates?.[0]?.groundingMetadata?.groundingChunks;
    if (groundingChunks && groundingChunks.length > 0) {
        const sources = groundingChunks
            .map(chunk => chunk.web && chunk.web.uri ? `[${chunk.web.title || chunk.web.uri}](${chunk.web.uri})` : null)
            .filter(Boolean);
        if (sources.length > 0) {
            resultText += "\n\n**Sources:**\n- " + sources.join('\n- ');
        }
    }

    return resultText;
};

/**
 * Calculate degree costs using the College Financial API
 * Supports three endpoints: /degree-cost, /compare, /program-cost
 * @param {Object} params - Parameters for the API call
 * @param {string} params.university - Single university name
 * @param {string} params.universities - Comma-separated list of universities for comparison
 * @param {string} params.program - Optional program/field of study
 * @param {string} params.degree - Optional degree level (default: "Bachelor")
 * @param {string} params.years - Optional number of years as string (default: "4")
 * @param {boolean} params.adjustInflation - Optional inflation adjustment (default: false)
 * @returns {Promise<string>} Formatted cost information
 */
export const getDegreeCost = async (params) => {
    const baseUrl = 'https://college-financial-api.gradtrack.workers.dev';
    let url;
    const queryParams = new URLSearchParams();

    // Determine which endpoint to use
    if (params.universities) {
        // Use /compare endpoint for multiple universities
        url = `${baseUrl}/compare`;
        queryParams.append('universities', params.universities);
    } else if (params.program && params.university) {
        // Use /program-cost endpoint for program-specific costs
        url = `${baseUrl}/program-cost`;
        queryParams.append('university', params.university);
        queryParams.append('program', params.program);
    } else if (params.university) {
        // Use /degree-cost endpoint for single university
        url = `${baseUrl}/degree-cost`;
        queryParams.append('university', params.university);
    } else {
        throw new Error('Either "university" or "universities" parameter is required');
    }

    // Add optional parameters
    if (params.degree) {
        queryParams.append('degree', params.degree);
    }
    if (params.years) {
        queryParams.append('years', params.years);
    }
    if (params.adjustInflation !== undefined) {
        queryParams.append('adjust_inflation', params.adjustInflation.toString());
    }

    const fullUrl = `${url}?${queryParams.toString()}`;
    
    try {
        const response = await fetch(fullUrl);
        
        if (!response.ok) {
            throw new Error(`API request failed: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();

        // Format the response for display
        let formattedResponse = '';

        if (data.comparisons) {
            // Comparison response
            formattedResponse = `**Degree Cost Comparison**\n\n`;
            formattedResponse += `Degree Level: ${data.degree}\n`;
            formattedResponse += `Years: ${data.years}\n`;
            formattedResponse += `Inflation Adjusted: ${data.inflation_adjusted ? 'Yes' : 'No'}\n\n`;
            
            if (data.cheapest_in_state) {
                formattedResponse += `💰 Cheapest In-State: ${data.cheapest_in_state}\n`;
            }
            if (data.cheapest_out_state) {
                formattedResponse += `💰 Cheapest Out-of-State: ${data.cheapest_out_state}\n`;
            }
            formattedResponse += '\n**Cost Breakdown:**\n\n';

            data.comparisons.forEach((comparison) => {
                formattedResponse += `**${comparison.university}**\n`;
                formattedResponse += `  In-State Total: $${comparison.in_state_total.toLocaleString()}\n`;
                formattedResponse += `  Out-of-State Total: $${comparison.out_of_state_total.toLocaleString()}\n`;
                if (comparison.breakdown) {
                    formattedResponse += `  - Tuition (In-State): $${comparison.breakdown.tuition_in_state?.toLocaleString() || 'N/A'}\n`;
                    formattedResponse += `  - Tuition (Out-of-State): $${comparison.breakdown.tuition_out_state?.toLocaleString() || 'N/A'}\n`;
                    formattedResponse += `  - Room & Board: $${comparison.breakdown.room_board?.toLocaleString() || 'N/A'}\n`;
                    formattedResponse += `  - Other Expenses: $${comparison.breakdown.other_expenses?.toLocaleString() || 'N/A'}\n`;
                }
                formattedResponse += '\n';
            });
        } else {
            // Single university or program response
            formattedResponse = `**Degree Cost Estimate**\n\n`;
            formattedResponse += `University: ${data.university}\n`;
            if (data.program) {
                formattedResponse += `Program: ${data.program}\n`;
            }
            formattedResponse += `Degree Level: ${data.degree}\n`;
            formattedResponse += `Years: ${data.years}\n`;
            formattedResponse += `Inflation Adjusted: ${data.inflation_adjusted ? 'Yes' : 'No'}\n\n`;
            
            formattedResponse += `**Total Costs:**\n`;
            formattedResponse += `  In-State: $${data.in_state_total.toLocaleString()}\n`;
            formattedResponse += `  Out-of-State: $${data.out_of_state_total.toLocaleString()}\n\n`;
            
            if (data.breakdown) {
                formattedResponse += `**Cost Breakdown:**\n`;
                formattedResponse += `  Tuition (In-State): $${data.breakdown.tuition_in_state?.toLocaleString() || 'N/A'}\n`;
                formattedResponse += `  Tuition (Out-of-State): $${data.breakdown.tuition_out_state?.toLocaleString() || 'N/A'}\n`;
                formattedResponse += `  Room & Board: $${data.breakdown.room_board?.toLocaleString() || 'N/A'}\n`;
                formattedResponse += `  Other Expenses: $${data.breakdown.other_expenses?.toLocaleString() || 'N/A'}\n`;
            }

            if (data.program_data) {
                formattedResponse += '\n';
                if (data.program_data.note) {
                    formattedResponse += `*${data.program_data.note}*\n`;
                }
            }
        }

        return formattedResponse;
    } catch (error) {
        console.error('Error fetching degree cost:', error);
        throw new Error(`Failed to calculate degree cost: ${error.message}`);
    }
};

/**
 * Search the college articulation PDF vector database
 * @param {Object} params - Parameters for the API call
 * @param {string} params.query - The search query or question
 * @param {number} params.topK - Number of relevant chunks to retrieve (default: 5)
 * @returns {Promise<string>} Formatted search results with source citations
 */
export const searchCollegeArticulationDocs = async (params) => {
    const baseUrl = 'https://articulation-vector-db-api.onrender.com';
    const url = `${baseUrl}/search`;
    
    const requestBody = {
        query: params.query,
        top_k: params.topK || 5
    };
    
    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody)
        });
        
        if (!response.ok) {
            throw new Error(`API request failed: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();

        // Format the response for display
        let formattedResponse = '';

        if (data.chunks && data.chunks.length > 0) {
            formattedResponse = `**Search Results**\n\n`;
            formattedResponse += `Found ${data.total_results || data.chunks.length} relevant document chunk(s) for: "${data.query}"\n\n`;
            
            // Use the pre-formatted context if available, otherwise format manually
            if (data.context) {
                formattedResponse += `**Relevant Information:**\n\n${data.context}\n\n`;
            } else {
                // Format chunks manually if context is not provided
                data.chunks.forEach((chunk, index) => {
                    formattedResponse += `**Result ${index + 1}** (Score: ${chunk.score?.toFixed(3) || 'N/A'})\n`;
                    formattedResponse += `Source: ${chunk.source || 'Unknown'}\n`;
                    formattedResponse += `${chunk.text}\n\n`;
                });
            }
            
            // Add source citations
            const sources = [...new Set(data.chunks.map(chunk => chunk.source).filter(Boolean))];
            if (sources.length > 0) {
                formattedResponse += `**Sources:** ${sources.join(', ')}\n`;
            }
        } else {
            formattedResponse = `No relevant information found for: "${data.query}"\n`;
            formattedResponse += `Please try rephrasing your question or using different keywords.`;
        }

        return formattedResponse;
    } catch (error) {
        console.error('Error searching articulation docs:', error);
        throw new Error(`Failed to search articulation documents: ${error.message}`);
    }
};